import { useState, useCallback } from "react";
import { Upload, X, FileText, Image, Check, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FileUploadZone({ label, hint, required, onFileUploaded, accept = ".pdf,.jpg,.jpeg,.png" }) {
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedFile({ ...data, name: file.name });
      onFileUploaded?.({ id: data.id, size: data.size });
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante il caricamento del file");
    } finally {
      setUploading(false);
    }
  }, [onFileUploaded]);

  const removeFile = () => {
    setUploadedFile(null);
    onFileUploaded?.(null);
  };

  const isImage = uploadedFile?.content_type?.startsWith("image/");
  const testId = `upload-zone-${(label || "file").toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="text-sm font-medium text-[#0F1F14]">
        {label} {required && <span className="text-[#D97B58]">*</span>}
      </label>
      {hint && <p className="text-xs text-[#4A5E51]">{hint}</p>}

      {!uploadedFile ? (
        <label className={`flex flex-col items-center gap-3 p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
          uploading ? 'border-[#163321] bg-[#163321]/5' : 'border-[#E5E2DB] hover:border-[#163321]/40 hover:bg-[#F5F4F0]'
        }`}>
          {uploading ? (
            <Loader2 className="w-8 h-8 text-[#163321] animate-spin" />
          ) : (
            <Upload className="w-8 h-8 text-[#4A5E51]" />
          )}
          <span className="text-sm text-[#4A5E51] text-center">
            {uploading ? "Caricamento in corso..." : "Clicca o trascina il file qui"}
          </span>
          <span className="text-xs text-[#4A5E51]/60">PDF, JPG, PNG (max 10MB)</span>
          <input type="file" accept={accept} onChange={handleUpload} className="hidden" disabled={uploading} data-testid={`${testId}-input`} />
        </label>
      ) : (
        <div className="flex items-center gap-3 p-4 bg-[#F5F4F0] rounded-xl border border-[#E5E2DB]">
          {isImage ? <Image className="w-5 h-5 text-[#163321] shrink-0" /> : <FileText className="w-5 h-5 text-[#163321] shrink-0" />}
          <span className="flex-1 text-sm text-[#0F1F14] truncate">{uploadedFile.name || "Documento caricato"}</span>
          <Check className="w-5 h-5 text-green-600 shrink-0" />
          <button onClick={removeFile} className="text-[#4A5E51] hover:text-[#D97B58] transition-colors" data-testid={`${testId}-remove`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && <p className="text-sm text-red-500" data-testid={`${testId}-error`}>{error}</p>}

      <p className="text-[10px] text-[#4A5E51]/50 leading-relaxed">
        I documenti caricati saranno utilizzati esclusivamente per la consulenza assicurativa. I tuoi dati sono al sicuro.
      </p>
    </div>
  );
}
